import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import api from "../../api/client";
import { ExpandableLeafletMap } from "../ExpandableLeafletMap";
import {
  DAYS,
  toDateStr,
  parseDateOnly,
  parseDateTime,
  startOfWeekMonday,
  endOfWeekSunday,
  formatNiceDate,
  formatNiceTime,
  fromDbTime,
  toDbTime,
  pointToLatLng,
} from "../../utils/schedule";

const AUTO_PREVIEW_DELAY_MS = 700;
const ADDRESS_AUTOCOMPLETE_DELAY_MS = 150;
const ADDRESS_AUTOCOMPLETE_LIMIT = 5;
const MIN_ADDRESS_AUTOCOMPLETE_CHARS = 3;

// Local alias retained so the date-input binding reads clearly.
const toLocalDateInputValue = toDateStr;

function normalizeLocationFromDb(loc) {
  const { lat, lng } = pointToLatLng(loc);
  return {
    id: loc.id,
    name: loc.name || "",
    address: loc.address || "",
    lat: lat || "",
    lng: lng || "",
    details: loc.details || "",
  };
}

function toCoordinateNumber(value) {
  if (value == null) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function hasCoordinates(value) {
  return toCoordinateNumber(value?.lat) != null && toCoordinateNumber(value?.lng) != null;
}

function getErrorMessage(error, fallback) {
  if (typeof error === "string" && error.trim()) return error;
  if (error?.message) return error.message;
  if (error?.error?.message) return error.error.message;
  return fallback;
}

function makeEmptyForm() {
  return {
    //schedule
    scheduleId: null,     //DB schedule id when editing
    date: toLocalDateInputValue(new Date()),
    startTime: "11:00",
    endTime: "14:00",
    //location selection / editing
    locationId: "",
    //optional "quick create location" fields
    locationName: "",
    address: "",
    lat: "",
    lng: "",
    notes: "",
  };
}

function makeEmptyAddressAutocomplete(query = "") {
  return {
    status: "idle",
    query,
    suggestions: [],
    message: "",
  };
}

function areRelatedAutocompleteQueries(previousQuery, nextQuery) {
  const prev = String(previousQuery || "").trim().toLowerCase();
  const next = String(nextQuery || "").trim().toLowerCase();
  if (!prev || !next) return false;
  return next.startsWith(prev) || prev.startsWith(next);
}

export const AdminScheduleEdit = () => {
  const [weekAnchor, setWeekAnchor] = useState(() => new Date());
  const [selectedDayIndex, setSelectedDayIndex] = useState(-1);

  const [mode, setMode] = useState("create"); // create | edit
  const [form, setForm] = useState(() => makeEmptyForm());
  const [showModal, setShowModal] = useState(false);

  // DB data
  const [locations, setLocations] = useState([]);
  const [scheduleRows, setScheduleRows] = useState([]);

  // UI state
  const [geocodePreview, setGeocodePreview] = useState({
    status: "idle",
    address: "",
    data: null,
    message: "",
  });
  const geocodeRequestIdRef = useRef(0);
  const [addressAutocomplete, setAddressAutocomplete] = useState(() => makeEmptyAddressAutocomplete());
  const [addressInputFocused, setAddressInputFocused] = useState(false);
  const [highlightedSuggestionIndex, setHighlightedSuggestionIndex] = useState(-1);
  const autocompleteRequestIdRef = useRef(0);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const weekStart = useMemo(() => startOfWeekMonday(weekAnchor), [weekAnchor]);
  const weekEnd = useMemo(() => endOfWeekSunday(weekAnchor), [weekAnchor]);

  const weekDays = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [weekStart]);

  const locationsById = useMemo(() => {
    const map = {};
    locations.forEach((l) => (map[l.id] = l));
    return map;
  }, [locations]);

  const selectedLocation = useMemo(() => {
    const id = Number(form.locationId);
    return locationsById[id] || null;
  }, [form.locationId, locationsById]);

  async function fetchLocations() {
    const res = await api.get("/locations");
    const list = Array.isArray(res.data) ? res.data : [];
    setLocations(list.map(normalizeLocationFromDb));
  }

  async function fetchScheduleForWeek(startDate, endDate) {
    const res = await api.get("/schedule", {
      params: { start: startDate, end: endDate },
    });
    setScheduleRows(Array.isArray(res.data) ? res.data : []);
  }

  useEffect(() => {
    const init = async () => {
      try {
        setError("");
        await fetchLocations();
      } catch (e) {
        console.error(e);
        setError("Failed to load locations");
      } finally {
      }
    };
    init();
  }, []);

  useEffect(() => {
    const run = async () => {
      try {
        setError("");
        await fetchScheduleForWeek(toDateStr(weekStart), toDateStr(weekEnd));
      } catch (e) {
        console.error(e);
        setError("Failed to load schedule");
        setScheduleRows([]);
      } finally {
      }
    };
    run();
  }, [weekStart, weekEnd]);

  // Expand schedule rows into UI stops (single-day assumed, but range-safe)
  const weekStops = useMemo(() => {
    const stops = [];

    for (const row of scheduleRows) {
      const loc = locationsById[row.location] || null;
      const startD = parseDateOnly(row.start_date);
      const endD = parseDateOnly(row.end_date);
      if (!startD || !endD) continue;

      for (let d = new Date(startD); d <= endD; d.setDate(d.getDate() + 1)) {
        const dateStr = toDateStr(d);
        const { lat, lng } = pointToLatLng({
          lat: loc?.lat ?? row.location_lat,
          lng: loc?.lng ?? row.location_lng,
          coords: loc?.coords,
        });

        stops.push({
          id: `${row.id}_${dateStr}`, // UI id
          scheduleId: row.id,
          locationId: row.location,
          title: loc?.name || row.location_name || "Stop",
          date: dateStr,
          startTime: fromDbTime(row.daily_start_time),
          endTime: fromDbTime(row.daily_end_time),
          address: loc?.address || row.location_address || "",
          lat: loc?.lat || lat || "",
          lng: loc?.lng || lng || "",
          notes: loc?.details || row.location_details || "",
        });
      }
    }

    // Filter to week bounds (safety)
    const bounded = stops.filter((s) => {
      const start = parseDateTime(s.date, s.startTime);
      return start >= weekStart && start <= weekEnd;
    });

    bounded.sort((a, b) => parseDateTime(a.date, a.startTime) - parseDateTime(b.date, b.startTime));
    return bounded;
  }, [scheduleRows, locationsById, weekStart, weekEnd]);

  const filteredStops = useMemo(() => {
    if (selectedDayIndex === -1) return weekStops;
    return weekStops.filter((s) => {
      const d = parseDateTime(s.date, s.startTime);
      const dayIdx = (d.getDay() + 6) % 7;
      return dayIdx === selectedDayIndex;
    });
  }, [weekStops, selectedDayIndex]);

  const weekGrid = useMemo(() => {
    const groups = Array.from({ length: 7 }, () => []);
    for (const s of weekStops) {
      const d = parseDateTime(s.date, s.startTime);
      const dayIdx = (d.getDay() + 6) % 7;
      groups[dayIdx].push(s);
    }
    return groups;
  }, [weekStops]);

  const manualLocationLocked = !!selectedLocation;
  const trimmedManualAddress = String(form.address || "").trim();
  const showAddressSuggestions =
    !manualLocationLocked && addressInputFocused && addressAutocomplete.suggestions.length > 0;
  const showAddressAutocompleteMessage =
    !manualLocationLocked &&
    addressInputFocused &&
    trimmedManualAddress.length >= MIN_ADDRESS_AUTOCOMPLETE_CHARS &&
    addressAutocomplete.suggestions.length === 0 &&
    !!addressAutocomplete.message &&
    addressAutocomplete.status !== "selected";

  const mapPreviewBase = useMemo(() => {
    if (selectedLocation) {
      return {
        id: `saved-${selectedLocation.id}`,
        title: selectedLocation.name || "Saved location",
        address: selectedLocation.address || "",
        lat: selectedLocation.lat || "",
        lng: selectedLocation.lng || "",
        notes: selectedLocation.details || "",
      };
    }

    return {
      id: form.scheduleId ? `draft-${form.scheduleId}` : "draft-location",
      title: form.locationName || "Draft location",
      address: form.address || "",
      lat: form.lat || "",
      lng: form.lng || "",
      notes: form.notes || "",
    };
  }, [
    selectedLocation,
    form.scheduleId,
    form.locationName,
    form.address,
    form.lat,
    form.lng,
    form.notes,
  ]);

  const mapPreviewStop = useMemo(() => {
    const lookupMatchesCurrentAddress =
      geocodePreview.status === "success" &&
      geocodePreview.address === String(mapPreviewBase.address || "").trim() &&
      geocodePreview.data;

    return {
      ...mapPreviewBase,
      lat:
        mapPreviewBase.lat ||
        (lookupMatchesCurrentAddress && geocodePreview.data?.lat != null
          ? String(geocodePreview.data.lat)
          : ""),
      lng:
        mapPreviewBase.lng ||
        (lookupMatchesCurrentAddress && geocodePreview.data?.lng != null
          ? String(geocodePreview.data.lng)
          : ""),
    };
  }, [mapPreviewBase, geocodePreview]);

  useEffect(() => {
    const address = String(mapPreviewBase.address || "").trim();
    const lat = toCoordinateNumber(mapPreviewBase.lat);
    const lng = toCoordinateNumber(mapPreviewBase.lng);

    if (!address) {
      geocodeRequestIdRef.current += 1;
      setGeocodePreview({
        status: "idle",
        address: "",
        data: null,
        message: "",
      });
      return undefined;
    }

    if (lat != null && lng != null) {
      geocodeRequestIdRef.current += 1;
      setGeocodePreview((prev) => {
        if (
          prev.status === "success" &&
          prev.address === address &&
          Number(prev.data?.lat) === lat &&
          Number(prev.data?.lng) === lng
        ) {
          return prev;
        }

        return {
          status: "success",
          address,
          data: { lat, lng, displayName: address },
          message: "Coordinates ready.",
        };
      });
      return undefined;
    }

    geocodeRequestIdRef.current += 1;
    setGeocodePreview((prev) => {
      if (
        prev.status === "idle" &&
        prev.address === address &&
        prev.data == null &&
        !prev.message
      ) {
        return prev;
      }

      return {
        status: "idle",
        address,
        data: null,
        message: "",
      };
    });
    return undefined;
  }, [mapPreviewBase.address, mapPreviewBase.lat, mapPreviewBase.lng]);

  useEffect(() => {
    if (manualLocationLocked) return undefined;

    const address = String(mapPreviewBase.address || "").trim();
    const lat = toCoordinateNumber(mapPreviewBase.lat);
    const lng = toCoordinateNumber(mapPreviewBase.lng);

    if (!address || (lat != null && lng != null)) {
      return undefined;
    }

    if (
      geocodePreview.address === address &&
      (geocodePreview.status === "loading" ||
        geocodePreview.status === "success" ||
        geocodePreview.status === "error")
    ) {
      return undefined;
    }

    const timer = window.setTimeout(async () => {
      const requestId = ++geocodeRequestIdRef.current;

      setGeocodePreview({
        status: "loading",
        address,
        data: null,
        message: "",
      });

      try {
        const res = await api.get("/locations/geocode", { params: { address } });
        const match = res?.data || null;
        if (geocodeRequestIdRef.current !== requestId) return;

        setGeocodePreview({
          status: "success",
          address,
          data: match,
          message: match?.displayName || "Coordinates ready.",
        });
      } catch (error) {
        if (geocodeRequestIdRef.current !== requestId) return;

        setGeocodePreview({
          status: "error",
          address,
          data: null,
          message: getErrorMessage(error, "Could not locate that address."),
        });
      }
    }, AUTO_PREVIEW_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [
    manualLocationLocked,
    mapPreviewBase.address,
    mapPreviewBase.lat,
    mapPreviewBase.lng,
    geocodePreview.address,
    geocodePreview.status,
  ]);

  useEffect(() => {
    if (!showModal || manualLocationLocked) {
      autocompleteRequestIdRef.current += 1;
      setHighlightedSuggestionIndex(-1);
      setAddressAutocomplete(makeEmptyAddressAutocomplete());
      return undefined;
    }

    const query = String(form.address || "").trim();
    const lat = toCoordinateNumber(form.lat);
    const lng = toCoordinateNumber(form.lng);

    if (query.length < MIN_ADDRESS_AUTOCOMPLETE_CHARS || (lat != null && lng != null)) {
      autocompleteRequestIdRef.current += 1;
      setHighlightedSuggestionIndex(-1);
      setAddressAutocomplete(makeEmptyAddressAutocomplete(query));
      return undefined;
    }

    const timer = window.setTimeout(async () => {
      const requestId = ++autocompleteRequestIdRef.current;
      setAddressAutocomplete((prev) => ({
        status: "loading",
        query,
        suggestions: areRelatedAutocompleteQueries(prev.query, query) ? prev.suggestions : [],
        message: "",
      }));

      try {
        const res = await api.get("/locations/autocomplete", {
          params: {
            text: query,
            limit: ADDRESS_AUTOCOMPLETE_LIMIT,
          },
        });

        if (autocompleteRequestIdRef.current !== requestId) return;

        const suggestions = Array.isArray(res?.data) ? res.data : [];
        setHighlightedSuggestionIndex(suggestions.length ? 0 : -1);
        setAddressAutocomplete({
          status: suggestions.length ? "success" : "empty",
          query,
          suggestions,
          message: suggestions.length ? "" : "No matching addresses found.",
        });
      } catch (error) {
        if (autocompleteRequestIdRef.current !== requestId) return;

        const message = getErrorMessage(error, "Address autocomplete is unavailable.");
        const status = String(message).toLowerCase().includes("geoapify") ? "unavailable" : "error";

        setHighlightedSuggestionIndex(-1);
        setAddressAutocomplete({
          status,
          query,
          suggestions: [],
          message,
        });
      }
    }, ADDRESS_AUTOCOMPLETE_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [showModal, manualLocationLocked, form.address, form.lat, form.lng]);

  function shiftWeek(deltaWeeks) {
    setWeekAnchor((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() + deltaWeeks * 7);
      return d;
    });
  }

  function quickSetToday() {
    const d = new Date();
    setWeekAnchor(d);
    setMode("create");
    setForm((prev) => ({ ...makeEmptyForm(), date: toDateStr(d) }));
  }

  function resetForm(toDate = toLocalDateInputValue(new Date())) {
    setMode("create");
    setForm({ ...makeEmptyForm(), date: toDate });
    setAddressInputFocused(false);
    setHighlightedSuggestionIndex(-1);
    setAddressAutocomplete(makeEmptyAddressAutocomplete());
  }

  function setFormField(key, value) {
    setForm((prev) => {
      if (key === "address" && !prev.locationId) {
        return { ...prev, address: value, lat: "", lng: "" };
      }
      return { ...prev, [key]: value };
    });
  }

  function applyLocation(loc) {
    setForm((prev) => ({
      ...prev,
      locationId: String(loc.id),
      locationName: loc.name || "",
      address: loc.address || "",
      lat: loc.lat || "",
      lng: loc.lng || "",
      notes: loc.details || "",
    }));

  }

  function clearLocation() {
    setForm((prev) => ({ ...prev, locationId: "", locationName: "", address: "", lat: "", lng: "", notes: "" }));
  }

  function applyAddressSuggestion(suggestion) {
    const nextAddress = String(suggestion?.formatted || suggestion?.address || "").trim();

    setForm((prev) => ({
      ...prev,
      address: nextAddress,
      lat: suggestion?.lat != null ? String(suggestion.lat) : "",
      lng: suggestion?.lng != null ? String(suggestion.lng) : "",
    }));

    autocompleteRequestIdRef.current += 1;
    setHighlightedSuggestionIndex(-1);
    setAddressAutocomplete({
      status: "selected",
      query: nextAddress,
      suggestions: [],
      message: suggestion?.secondaryText || "",
    });
  }

  function handleAddressKeyDown(event) {
    if (!addressAutocomplete.suggestions.length) {
      if (event.key === "Escape") {
        setAddressInputFocused(false);
      }
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedSuggestionIndex((prev) =>
        prev < addressAutocomplete.suggestions.length - 1 ? prev + 1 : 0
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedSuggestionIndex((prev) =>
        prev > 0 ? prev - 1 : addressAutocomplete.suggestions.length - 1
      );
      return;
    }

    if (event.key === "Enter" && showAddressSuggestions && highlightedSuggestionIndex >= 0) {
      event.preventDefault();
      applyAddressSuggestion(addressAutocomplete.suggestions[highlightedSuggestionIndex]);
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setHighlightedSuggestionIndex(-1);
      setAddressInputFocused(false);
    }
  }

  async function ensureLocationId() {
    // If user selected an existing locationId, use it.
    const locId = String(form.locationId || "").trim();
    if (locId) {
      const existingLocationId = Number(locId);

      try {
        if (
          selectedLocation &&
          !hasCoordinates(selectedLocation) &&
          geocodePreview.status === "success" &&
          geocodePreview.data?.lat != null &&
          geocodePreview.data?.lng != null
        ) {
          await api.put(`/locations/${existingLocationId}`, {
            name: selectedLocation.name || selectedLocation.address || `Location ${existingLocationId}`,
            address: selectedLocation.address || "",
            details: selectedLocation.details || "",
            lat: Number(geocodePreview.data.lat),
            lng: Number(geocodePreview.data.lng),
          });
          await fetchLocations();
        }
      } catch (error) {
        console.error(error);
        alert(getErrorMessage(error, "Failed to update location coordinates."));
        return null;
      }

      return existingLocationId;
    }

    // Otherwise, try to create a new location from fields
    const name = (form.locationName || "").trim();
    const address = (form.address || "").trim();
    const details = (form.notes || "").trim();
    const lat = String(form.lat || "").trim();
    const lng = String(form.lng || "").trim();

    if (!name) {
      alert("Pick a saved location OR enter a Location Name to create one.");
      return null;
    }
    if (!address) {
      alert("Enter an address for the new location.");
      return null;
    }

    const hasLat = lat !== "";
    const hasLng = lng !== "";
    if (hasLat !== hasLng) {
      alert("Enter both latitude and longitude, or leave both blank.");
      return null;
    }

    try {
      const payload = {
        name,
        address,
        details,
        lat: hasLat ? Number(lat) : null,
        lng: hasLng ? Number(lng) : null,
      };

      const res = await api.post("/locations", payload);
      const created = res.data;

      // Refresh list (safer)
      await fetchLocations();

      // Best guess: created record returns an id
      return created?.id ?? null;
    } catch (e) {
      console.error(e);
      alert(getErrorMessage(e, "Failed to create location."));
      return null;
    }
  }

  async function onSubmit(e) {
    e.preventDefault();

    // Validate schedule
    if (!form.date) {
      alert("Please select a date.");
      return;
    }
    if (!form.startTime || !form.endTime) {
      alert("Please enter a start and end time.");
      return;
    }
    const start = parseDateTime(form.date, form.startTime);
    const end = parseDateTime(form.date, form.endTime);
    if (end <= start) {
      alert("End time must be after start time.");
      return;
    }

    const locationId = await ensureLocationId();
    if (!locationId) return;

    const payload = {
      location: locationId,
      start_date: form.date,
      end_date: form.date, // single-day row for simplicity
      daily_start_time: toDbTime(form.startTime),
      daily_end_time: toDbTime(form.endTime),
    };

    try {
      setSaving(true);
      setError("");

      if (mode === "create") {
        await api.post("/schedule", payload);
      } else {
        if (!form.scheduleId) {
          alert("Missing scheduleId for edit.");
          return;
        }
        await api.put(`/schedule/${form.scheduleId}`, payload);
      }

      await fetchScheduleForWeek(toDateStr(weekStart), toDateStr(weekEnd));
      resetForm(form.date);
      setShowModal(false);
    } catch (e2) {
      console.error(e2);
      setError("Failed to save schedule");
      alert("Failed to save schedule. Check server logs / endpoint payload.");
    } finally {
      setSaving(false);
    }
  }

  async function deleteLocation(loc) {
    if (!window.confirm(`Delete "${loc.name || loc.address}"? This cannot be undone.`)) return;
    try {
      setSaving(true);
      await api.delete(`/locations/${loc.id}`);
      if (String(form.locationId) === String(loc.id)) clearLocation();
      await fetchLocations();
    } catch (e) {
      console.error(e);
      alert(getErrorMessage(e, "Failed to delete location."));
    } finally {
      setSaving(false);
    }
  }

  function openCreate() {
    resetForm();
    setShowModal(true);
  }

  function startEdit(stop) {
    const loc = locationsById[stop.locationId] || null;

    setMode("edit");
    setAddressInputFocused(false);
    setHighlightedSuggestionIndex(-1);
    setAddressAutocomplete(makeEmptyAddressAutocomplete());
    setForm((prev) => ({
      ...prev,
      scheduleId: stop.scheduleId,
      date: stop.date,
      startTime: stop.startTime,
      endTime: stop.endTime,
      locationId: String(stop.locationId || ""),
      locationName: loc?.name || stop.title || "",
      address: stop.address || "",
      lat: stop.lat || "",
      lng: stop.lng || "",
      notes: stop.notes || "",
    }));
    setShowModal(true);
  }

  async function removeStop(stop) {
    if (!window.confirm("Delete this stop?")) return;
    try {
      setSaving(true);
      await api.delete(`/schedule/${stop.scheduleId}`);
      await fetchScheduleForWeek(toDateStr(weekStart), toDateStr(weekEnd));
      if (mode === "edit" && form.scheduleId === stop.scheduleId) resetForm();
    } catch (e) {
      console.error(e);
      alert("Failed to delete stop.");
    } finally {
      setSaving(false);
    }
  }

  async function duplicateStop(stop) {
    try {
      setSaving(true);
      const payload = {
        location: stop.locationId,
        start_date: stop.date,
        end_date: stop.date,
        daily_start_time: toDbTime(stop.startTime),
        daily_end_time: toDbTime(stop.endTime),
      };
      await api.post("/schedule", payload);
      await fetchScheduleForWeek(toDateStr(weekStart), toDateStr(weekEnd));
    } catch (e) {
      console.error(e);
      alert("Failed to duplicate stop.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="as-wrap">
      <header className="as-header">
        <div>
          <Link to="/admin" className="as-admin-back">← Admin</Link>
          <h1 className="as-title">Schedule</h1>
          <p className="as-subtitle">
            Add weekly stops for the food truck.
          </p>
          {error ? <p className="as-subtitle" style={{ color: "crimson" }}>{error}</p> : null}
{saving ? <p className="as-subtitle">Saving…</p> : null}
        </div>

        <div className="as-headerActions">
          <button className="as-headerBtn as-headerBtnPrimary" type="button" onClick={openCreate}>
            Add stop
          </button>
          <button className="as-headerBtn" type="button" onClick={quickSetToday}>
            This week
          </button>
        </div>
      </header>

      <main className="as-main">
        {/* LEFT: Week at a glance */}
        <section className="as-card">
          <div className="as-cardHeader">
            <h2 className="as-cardTitle">Week at a glance</h2>
            <div className="as-weekNav">
              <button className="as-weekBtn" type="button" onClick={() => shiftWeek(-1)} aria-label="Previous week">
                &larr;
              </button>
              <div className="as-weekRange">
                {weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })} –{" "}
                {weekEnd.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
              </div>
              <button className="as-weekBtn" type="button" onClick={() => shiftWeek(1)} aria-label="Next week">
                &rarr;
              </button>
            </div>
          </div>

          <div className="as-weekGrid">
            {weekDays.map((dayDate, idx) => {
              const dayStr = toLocalDateInputValue(dayDate);
              const items = weekGrid[idx];
              const isSelected = selectedDayIndex === idx;

              return (
                <button
                  key={dayStr}
                  className={`as-dayCol ${isSelected ? "is-selected" : ""}`}
                  type="button"
                  onClick={() => {
                    setSelectedDayIndex(selectedDayIndex === idx ? -1 : idx);
                    setFormField("date", dayStr);
                  }}
                >
                  <div className="as-dayTop">
                    <div className="as-dayName">{DAYS[idx]}</div>
                    <div className="as-dayDate">
                      {dayDate.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </div>
                  </div>

                  <div className="as-dayItems">
                    {items.length === 0 ? (
                      <div className="as-empty">No stops</div>
                    ) : (
                      items.slice(0, 3).map((s) => {
                        const start = parseDateTime(s.date, s.startTime);
                        const end = parseDateTime(s.date, s.endTime);
                        return (
                          <div
                            key={s.id}
                            className="as-miniItem"
                            title={`${s.title} · ${formatNiceTime(start)}–${formatNiceTime(end)} · ${s.address}`}
                          >
                            <div className="as-miniTime">
                              {formatNiceTime(start)}–{formatNiceTime(end)}
                            </div>
                            <div className="as-miniTitle">{s.title}</div>
                          </div>
                        );
                      })
                    )}
                    {items.length > 3 && <div className="as-more">+{items.length - 3} more</div>}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* MIDDLE: Stop list */}
        <section className="as-card">
          <div className="as-cardHeader">
            <h2 className="as-cardTitle">Stops</h2>
            <p className="as-cardNote">{filteredStops.length} in view</p>
          </div>

          <div className="as-stopList">
            {filteredStops.length === 0 ? (
              <div className="as-emptyBig">No stops match this week/filter.</div>
            ) : (
              filteredStops.map((s) => {
                const start = parseDateTime(s.date, s.startTime);
                const end = parseDateTime(s.date, s.endTime);
                return (
                  <div key={s.id} className="as-stopRow">
                    <div className="as-stopMain">
                      <div className="as-stopTitle">{s.title}</div>

                      <div className="as-stopMeta">
                        <span>{formatNiceDate(start)}</span>
                        <span className="as-dot">•</span>
                        <span>{formatNiceTime(start)}–{formatNiceTime(end)}</span>
                        <span className="as-dot">•</span>
                        <span className="as-stopAddress">{s.address || "No address yet"}</span>
                      </div>

                      {s.notes ? <div className="as-stopNotes">{s.notes}</div> : null}
                      {(s.lat || s.lng) ? (
                        <div className="as-stopCoords">
                          Coords: {s.lat || "—"}, {s.lng || "—"}
                        </div>
                      ) : null}
                    </div>

                    <div className="as-stopActions">
                      <button className="as-btnSmall" type="button" onClick={() => startEdit(s)}>
                        Edit
                      </button>
                      <button className="as-btnSmall" type="button" onClick={() => duplicateStop(s)}>
                        Duplicate
                      </button>
                      <button className="as-btnSmall as-btnDanger" type="button" onClick={() => removeStop(s)}>
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>

      </main>

      {showModal && (
        <div className="as-overlay" onClick={() => setShowModal(false)}>
          <div className="as-modal" onClick={(e) => e.stopPropagation()}>
            <div className="as-modalHeader">
              <h2 className="as-cardTitle">{mode === "create" ? "Add stop" : "Edit stop"}</h2>
              <button className="as-modalClose" type="button" onClick={() => setShowModal(false)} aria-label="Close">
                &times;
              </button>
            </div>

            <form className="as-form" onSubmit={onSubmit}>

              <h3 className="as-sectionHeading">Stop</h3>

              <div className="as-formRow">
                <div className="as-field">
                  <label className="as-label" htmlFor="date">Date</label>
                  <input
                    id="date"
                    type="date"
                    className="as-input"
                    value={form.date}
                    onChange={(e) => setFormField("date", e.target.value)}
                  />
                </div>
              </div>

              <div className="as-formRow">
                <div className="as-field">
                  <label className="as-label" htmlFor="start">Start</label>
                  <input
                    id="start"
                    type="time"
                    className="as-input"
                    value={form.startTime}
                    onChange={(e) => setFormField("startTime", e.target.value)}
                  />
                </div>

                <div className="as-field">
                  <label className="as-label" htmlFor="end">End</label>
                  <input
                    id="end"
                    type="time"
                    className="as-input"
                    value={form.endTime}
                    onChange={(e) => setFormField("endTime", e.target.value)}
                  />
                </div>
              </div>

              <h3 className="as-sectionHeading">Location</h3>

              <div className="as-locPicker">
                <div className="as-locList">
                  <button
                    type="button"
                    className={`as-locRow ${!form.locationId ? "is-selected" : ""}`}
                    onClick={clearLocation}
                  >
                    <div className="as-locName">New location</div>
                    <div className="as-locAddr">Create from the fields below</div>
                  </button>
                  {locations.map((loc) => {
                    const isSelected = String(loc.id) === String(form.locationId);
                    return (
                      <div key={loc.id} className={`as-locRow ${isSelected ? "is-selected" : ""}`}>
                        <button
                          type="button"
                          className="as-locRowBody"
                          onClick={() => applyLocation(loc)}
                        >
                          <div className="as-locName">{loc.name || loc.address || `Location #${loc.id}`}</div>
                          <div className="as-locAddr">{loc.address || "No address"}</div>
                        </button>
                        <button
                          type="button"
                          className="as-locDelete"
                          onClick={() => deleteLocation(loc)}
                          disabled={saving}
                          aria-label={`Delete ${loc.name || "location"}`}
                        >
                          &times;
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {!manualLocationLocked && (
                <>
                  <div className="as-field">
                    <label className="as-label" htmlFor="locationName">Location name</label>
                    <input
                      id="locationName"
                      className="as-input"
                      value={form.locationName}
                      onChange={(e) => setFormField("locationName", e.target.value)}
                      placeholder="e.g., Downtown Square"
                    />
                  </div>

                  <div className="as-field">
                    <label className="as-label" htmlFor="address">Address</label>
                    <div className="as-autocompleteWrap">
                      <input
                        id="address"
                        className="as-input"
                        value={form.address}
                        onChange={(e) => setFormField("address", e.target.value)}
                        onFocus={() => setAddressInputFocused(true)}
                        onBlur={() => {
                          setAddressInputFocused(false);
                          setHighlightedSuggestionIndex(-1);
                        }}
                        onKeyDown={handleAddressKeyDown}
                        placeholder="123 Main St, City, ST"
                        autoComplete="off"
                        role="combobox"
                        aria-autocomplete="list"
                        aria-expanded={showAddressSuggestions}
                        aria-controls="address-autocomplete-list"
                        aria-activedescendant={
                          showAddressSuggestions && highlightedSuggestionIndex >= 0
                            ? `address-autocomplete-option-${highlightedSuggestionIndex}`
                            : undefined
                        }
                      />

                      {showAddressSuggestions ? (
                        <div className="as-autocompleteList" id="address-autocomplete-list" role="listbox">
                          {addressAutocomplete.suggestions.map((suggestion, index) => (
                            <button
                              key={`${suggestion.formatted}-${suggestion.lat}-${suggestion.lng}`}
                              id={`address-autocomplete-option-${index}`}
                              type="button"
                              className={`as-autocompleteOption ${index === highlightedSuggestionIndex ? "is-active" : ""}`}
                              role="option"
                              aria-selected={index === highlightedSuggestionIndex}
                              onMouseDown={(event) => {
                                event.preventDefault();
                                applyAddressSuggestion(suggestion);
                              }}
                            >
                              <span className="as-autocompletePrimary">{suggestion.formatted}</span>
                              {suggestion.secondaryText ? (
                                <span className="as-autocompleteSecondary">{suggestion.secondaryText}</span>
                              ) : null}
                            </button>
                          ))}
                        </div>
                      ) : null}

                      {showAddressAutocompleteMessage ? (
                        <div className="as-helperText">{addressAutocomplete.message}</div>
                      ) : null}
                    </div>
                  </div>

                  <div className="as-field">
                    <label className="as-label" htmlFor="notes">Details</label>
                    <textarea
                      id="notes"
                      className="as-textarea"
                      value={form.notes}
                      onChange={(e) => setFormField("notes", e.target.value)}
                      placeholder="e.g., Park on the east side"
                    />
                  </div>
                </>
              )}

              <div className="as-field as-mapField">
                <div className="as-label">Location preview</div>
                <ExpandableLeafletMap
                  markers={[mapPreviewStop]}
                  selectedId={mapPreviewStop.id}
                  onSelect={() => {}}
                  emptyTitle="No location to preview"
                  emptyText="Select a saved location or enter an address."
                />
              </div>

              <div className="as-formActions">
                <button className="as-btnPrimary" type="submit" disabled={saving}>
                  {mode === "create" ? "Add stop" : "Save changes"}
                </button>

                <button className="as-btn" type="button" onClick={() => setShowModal(false)} disabled={saving}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

